import type { DayPart, RequestStatus } from "./shared/timeOffTypes";
import { parseDateOnly } from "./dateOnly";

export function formatDateRange(startDate: string, endDate: string, dayPart: DayPart): string {
  const fmt = (iso: string) =>
    parseDateOnly(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const range = startDate === endDate ? fmt(startDate) : `${fmt(startDate)} – ${fmt(endDate)}`;
  if (dayPart === "half-am") return `${range} (morning)`;
  if (dayPart === "half-pm") return `${range} (afternoon)`;
  return range;
}

/** Formats a full ISO datetime (decided_at, submitted_at -- unlike the date-only start/end fields) for display, e.g. in the Acknowledgements and Org-wide tables. */
export function formatTimestamp(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export const STATUS_LABEL: Record<RequestStatus, string> = {
  pending: "Pending",
  approved: "Acknowledged",
  denied: "Needs Coordination",
  cancelled: "Cancelled",
};

/**
 * Who to show as having acknowledged a request, for the Org-wide "All
 * requests" table. Prefers the actual decider (decided_by_name/email) when
 * there is one. Sick Time is auto-approved on submission and never gets a
 * decided_by_email, so for that case only, falls back to the requester's
 * manager -- that's who it would have gone to if it needed a decision.
 * Anything else undecided (pending, or cancelled before a decision was
 * made) shows an em dash.
 */
export function acknowledgedByLabel(request: {
  status: RequestStatus;
  auto_approved: boolean;
  decided_by_email: string | null;
  decided_by_name?: string | null;
  requester_manager_name?: string | null;
}): string {
  if (request.decided_by_name) return request.decided_by_name;
  if (request.decided_by_email) return request.decided_by_email;
  if (request.auto_approved && request.status === "approved") {
    return request.requester_manager_name ?? "—";
  }
  return "—";
}

export function firstName(fullName: string): string {
  return fullName.split(" ")[0] ?? fullName;
}
